/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { TestDriveBannerAtom } from "../atoms/TestDriveBannerAtom";
import StripeProvider from "../components/Modal/container/Billing/StripeProvider";
import SeoHead from "../components/SeoHead";
import Blocking from "../components/Settings/Blocking";
import ContactsSettings from "../components/Settings/ContactsSettings";
import DNSRecords from "../components/Settings/DNSRecords";
import EmailSettings from "../components/Settings/EmailSettings";
import General from "../components/Settings/General";
import Header from "../components/Settings/Header";
import Integration from "../components/Settings/Integration";
import Integrations from "../components/Settings/Integrations/Integrations";
import Learnistic from "../components/Settings/Integrations/Learnistics";
import Thrivecart from "../components/Settings/Integrations/Thrivecart";
import ThrivecartProducts from "../components/Settings/Integrations/ThrivecartProducts";
import Notifications from "../components/Settings/Notifications";
import Security from "../components/Settings/Security";
import Categories from "../components/Settings/Tags/Categories";
import Tags from "../components/Settings/Tags/Tags";
import TopBar from "../components/Settings/TopBar";
import { localStorageGetItem } from "../utils/LocalStorage";
import Stripe from "../components/Settings/Integrations/Stripe";
import ExternalUsersContact from "./users";
import StripeProducts from "../components/Settings/Integrations/StripeProducts";
import Wordpress from "../components/Settings/Integrations/Wordpress";
import Shopify from "../components/Settings/Integrations/Shopify";
import ShopifyProducts from "../components/Settings/Integrations/ShopifyProducts";
import WordpressCategories from "../components/Settings/Integrations/WordpressCategories";

const TableContainer = styled.div`
  height: fit-content;

  padding: 0 24px;

  overflow-y: auto;

  display: flex;
  justify-content: center;

  &.blocking {
    @media screen and (max-width: 1140px) {
      justify-content: start;
    }
  }

  @media screen and (max-width: 1140px) {
    padding: 0 16px;
  }
`;

const Body = styled.div<{ showTestDrive?: boolean }>`
  width: 100%;
  height: ${(props) =>
    props.showTestDrive ? "calc(100vh - 158px)" : "calc(100vh - 83px)"};

  margin-top: 83px;

  display: flex;
  flex-direction: column;

  background-color: #f9f9f9;
`;

export enum SettingsTab {
  GENERAL = "GENERAL",
  BILLING = "BILLING",
  CONTACTS = "CONTACTS",
  SECURITY = "SECURITY",
  NOTIFICATIONS = "NOTIFICATIONS",
  API_KEY = "API_KEY",
  TAGS = "TAGS",
  LINKS = "LINKS",
  CATEGORIES = "CATEGORIES",
  BLOCKING = "BLOCKING",
  USERS = "USERS",
  INTEGRATIONS = "INTEGRATIONS",
  LEARNISTIC = "LEARNISTIC",
  WORDPRESS = "WORDPRESS",
  WORDPRESSCATEGORIES = "WORDPRESSCATEGORIES",
  SHOPIFY = "SHOPIFY",
  SHOPIFYPRODUCTS = "SHOPIFYPRODUCTS",
  STRIPE = "STRIPE",
  STRIPEPRODUCTS = "STRIPEPRODUCTS",
  THRIVECART = "THRIVECART",
  THRIVECARTPRODUCTS = "THRIVECARTPRODUCTS",
  DNS_RECORDS = "DNS_RECORDS",
  EMAIL = "EMAIL",
}

export default function SettingsScreen(): JSX.Element {
  const router = useRouter();

  const [selected, setSelected] = useState(SettingsTab.API_KEY);
  const showTestDrive = useRecoilValue(TestDriveBannerAtom);

  useEffect(() => {
    if (localStorageGetItem("ADMIN_UID")) {
      setSelected(SettingsTab.SECURITY);
    }
  }, []);

  useEffect(() => {
    if (router.query.screen) {
      setSelected(router.query.screen as SettingsTab);
    }
  }, [router.query]);

  function handleSelected(vle: SettingsTab) {
    setSelected(vle);
  }

  function returnBody(screen: SettingsTab) {
    switch (screen) {
      case SettingsTab.GENERAL:
        return <General />;
      case SettingsTab.CONTACTS:
        return <ContactsSettings />;
      case SettingsTab.BILLING:
        return (
          <StripeProvider
            isModal
            onCancelClicked={() => {
              setSelected(SettingsTab.GENERAL);
            }}
            onOkClicked={() => {}}
          />
        );
      case SettingsTab.SECURITY:
        return <Security />;
      case SettingsTab.NOTIFICATIONS:
        return <Notifications />;
      case SettingsTab.API_KEY:
        return <Integration />;
      case SettingsTab.TAGS:
        return <Tags />;
      case SettingsTab.CATEGORIES:
        return <Categories />;
      case SettingsTab.BLOCKING:
        return <Blocking />;
      case SettingsTab.USERS:
        return <ExternalUsersContact />;
      case SettingsTab.INTEGRATIONS:
        return <Integrations handleSelected={handleSelected} />;
      case SettingsTab.LEARNISTIC:
        return <Learnistic />;
      case SettingsTab.STRIPE:
        return <Stripe handleSelected={handleSelected} />;
      case SettingsTab.STRIPEPRODUCTS:
        return <StripeProducts handleSelected={handleSelected} />;
      case SettingsTab.WORDPRESS:
        return <Wordpress handleSelected={handleSelected} />;
      case SettingsTab.SHOPIFY:
        return <Shopify handleSelected={handleSelected} />;
      case SettingsTab.SHOPIFYPRODUCTS:
        return <ShopifyProducts handleSelected={handleSelected} />;
      case SettingsTab.THRIVECART:
        return <Thrivecart handleSelected={handleSelected} />;
      case SettingsTab.THRIVECARTPRODUCTS:
        return <ThrivecartProducts handleSelected={handleSelected} />;
      case SettingsTab.WORDPRESSCATEGORIES:
        return <WordpressCategories handleSelected={handleSelected} />;
      case SettingsTab.DNS_RECORDS:
        return <DNSRecords />;
      case SettingsTab.EMAIL:
        return <EmailSettings />;
      default:
    }
    return <General />;
  }

  return (
    <>
      <SeoHead pageName="Settings" />
      <TopBar page={selected} />
      <Body showTestDrive={showTestDrive.show}>
        <Header selected={selected} handleSelected={handleSelected} />
        <TableContainer
          className={selected === SettingsTab.BLOCKING ? "blocking" : ""}
        >
          {returnBody(selected)}
        </TableContainer>
      </Body>
    </>
  );
}
